import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import ArticlePropsService from "@core-ui/ContextServices/ArticleProps";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import CategoryItemFilter from "@ext/catalog/views/logic/utils/CategoryItemFilter";
import { navigationTreeStore } from "@ext/navigation/catalog/SidebarNavigation/store/navigationTreeStore";
import PropertyServiceProvider from "@ext/properties/components/PropertyService";
import { deleteProperty, updateProperty } from "@ext/properties/logic/changeProperty";
import combineProperties from "@ext/properties/logic/combineProperties";
import { saveArticleProps } from "@ext/properties/logic/saveArticleProperties";
import type { Property } from "@ext/properties/models";
import { type Dispatch, type SetStateAction, useCallback } from "react";

interface UseUpdateArticlePropertyProps {
	setProperties: Dispatch<SetStateAction<Property[]>>;
}

const itemFilter = new CategoryItemFilter();

export const useUpdateArticleProperty = (props: UseUpdateArticlePropertyProps) => {
	const { setProperties } = props;
	const articleProps = ArticlePropsService.value;
	const { properties: catalogProperties } = PropertyServiceProvider.value;
	const apiUrlCreator = ApiUrlCreator.value;
	const resolvedView = useCatalogPropsStore((state) => state.data?.resolvedView);

	// biome-ignore lint/correctness/useExhaustiveDependencies: articleProps comes from a context service, so its path is reactive
	const reportViewMatch = useCallback(
		(properties: Property[]) => {
			const path = articleProps?.ref?.path;
			if (!resolvedView || !path) return;
			navigationTreeStore
				.getState()
				.markFilteredOut(path, !itemFilter.matchesFilter({ props: { properties } }, resolvedView));
		},
		[articleProps?.ref?.path, resolvedView],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: values are read inside the state updater
	const updateHandler = useCallback(
		(id: string, value?: string) => {
			setProperties((prevProps: Property[]) => {
				const newProps = updateProperty(id, value, catalogProperties, prevProps);
				if (!newProps) return prevProps;

				void saveArticleProps(apiUrlCreator, { ...articleProps, properties: newProps });

				reportViewMatch(newProps as Property[]);
				return combineProperties(newProps, catalogProperties);
			});
		},
		[articleProps, catalogProperties, apiUrlCreator, setProperties, reportViewMatch],
	);

	const onSubmit = useCallback(
		(id: string, value: string) => {
			updateHandler(id, value);
		},
		[updateHandler],
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: values are read inside the state updater
	const onDelete = useCallback(
		(id: string) => {
			setProperties((prevProps: Property[]) => {
				const newProps = deleteProperty(id, prevProps) as Property[];

				void saveArticleProps(apiUrlCreator, { ...articleProps, properties: newProps });

				reportViewMatch(newProps as Property[]);
				return combineProperties(newProps, catalogProperties);
			});
		},
		[articleProps, catalogProperties, apiUrlCreator, setProperties, reportViewMatch],
	);

	return { onSubmit, onDelete };
};
